import {useEffect, useLayoutEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

export * from './shared';

type PrerenderWindow = Window & {prerenderReady?: boolean};

/**
 * Coordinate snapshots taken by prerender.io for crawler-facing routes.
 *
 * Setting `window.prerenderReady = false` tells prerender to hold the snapshot
 * until we flip it to `true`. We do that one paint after `contentReady` so that
 * layout-driven children (e.g. the inference Explorer grid, which only mounts
 * once its ResizeObserver reports a non-zero width) are present in the captured
 * HTML. For ordinary visitors this flag is inert. Only use this on routes that
 * are whitelisted for prerendering in the host (`host/src/app.ts`); otherwise a
 * page that never reaches `contentReady` would force prerender to wait for its
 * full timeout.
 */
export function usePrerenderReady(contentReady: boolean): void {
  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      (window as PrerenderWindow).prerenderReady = false;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !contentReady) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      (window as PrerenderWindow).prerenderReady = true;
    });
    return () => cancelAnimationFrame(raf);
  }, [contentReady]);
}

export function setDocumentTitle(title: string, appendWB = true): void {
  if (title === 'undefined') {
    document.title = 'Weights & Biases';
    return;
  }
  if (appendWB) {
    title += ' – Weights & Biases';
  }
  document.title = title;
}

export function setDocumentDescription(desc: string): void {
  let metaEl: HTMLMetaElement | null = document.querySelector(
    `meta[name="description"]`
  );
  if (metaEl == null) {
    metaEl = document.createElement('meta');
    metaEl.name = 'description';
    document.head.append(metaEl);
  }
  metaEl.content = desc;
}

export function useScrollToURLHash(delayMS = 1000): void {
  const hashId = useLocation().hash.slice(1);
  const scrolledRef = useRef('');
  useEffect(() => {
    if (hashId === '') {
      scrolledRef.current = '';
      return;
    }
    if (scrolledRef.current === hashId) {
      return;
    }
    const element = document.getElementById(hashId);
    if (element == null) {
      return;
    }
    scrolledRef.current = hashId;
    setTimeout(() => element.scrollIntoView({behavior: 'smooth'}), delayMS);
  });
}

export const scrollToElement = (elementSelector: string) => {
  const el = document.querySelector(elementSelector);
  if (el != null) {
    el.scrollIntoView({
      inline: 'center',
      behavior: 'smooth',
    });
  }
};

export const scrollToElementByID = (id: string) => {
  const el = document.getElementById(id);
  if (el != null) {
    el.scrollIntoView({
      inline: 'center',
      block: 'center',
      behavior: 'smooth',
    });
  }
};
